/** Wall dwell below this fraction of configured slot duration counts as accelerated (#579). */
export const FRAME_TRANSITION_ACCELERATED_MAX_DWELL_FRACTION = 0.5

/** Ignore very short configured slots (registration/bootstrap glitches). */
export const FRAME_TRANSITION_ACCELERATED_MIN_SLOT_MS = 2000

/** Keep fault active this long after a fast transition so heartbeats can surface it. */
export const FRAME_TRANSITION_ACCELERATED_ACTIVE_WINDOW_MS = 60_000

export interface ILastCompletedFrameTransition {
  atMs: number
  dwellMs: number
  durationMs: number
  frameId: string
}

export interface IFrameTransitionAcceleratedDetectInput {
  nowMs: number
  documentVisible: boolean
  queueLength: number
  lastCompletedTransition: ILastCompletedFrameTransition | null
  suppressBecauseRegistrationResync: boolean
  suppressBecauseRegistrationTransitionHold: boolean
}

export interface IFrameTransitionAcceleratedStatus {
  suspected: boolean
  /** Ms the completed frame ended early (detail for fault / Sentry). */
  earlyByMs: number
  dwellMs: number
  configuredDurationMs: number
  frameId: string | null
}

const INACTIVE: IFrameTransitionAcceleratedStatus = {
  suspected: false,
  earlyByMs: 0,
  dwellMs: 0,
  configuredDurationMs: 0,
  frameId: null
}

export function detectFrameTransitionAccelerated(
  input: IFrameTransitionAcceleratedDetectInput
): IFrameTransitionAcceleratedStatus {
  const {
    nowMs,
    documentVisible,
    queueLength,
    lastCompletedTransition,
    suppressBecauseRegistrationResync,
    suppressBecauseRegistrationTransitionHold
  } = input

  if (
    !documentVisible
    || queueLength < 1
    || lastCompletedTransition == null
    || suppressBecauseRegistrationResync
    || suppressBecauseRegistrationTransitionHold
  ) {
    return INACTIVE
  }

  const ageMs = Math.max(0, nowMs - lastCompletedTransition.atMs)
  if (ageMs > FRAME_TRANSITION_ACCELERATED_ACTIVE_WINDOW_MS) {
    return INACTIVE
  }

  const { dwellMs, durationMs, frameId } = lastCompletedTransition
  if (durationMs < FRAME_TRANSITION_ACCELERATED_MIN_SLOT_MS || dwellMs <= 0) {
    return INACTIVE
  }

  const minDwellMs = durationMs * FRAME_TRANSITION_ACCELERATED_MAX_DWELL_FRACTION
  if (dwellMs >= minDwellMs) {
    return INACTIVE
  }

  const earlyByMs = Math.max(0, Math.round(minDwellMs - dwellMs))
  return {
    suspected: true,
    earlyByMs,
    dwellMs,
    configuredDurationMs: durationMs,
    frameId
  }
}
